import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Heart,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  ArrowRight,
  AlertCircle,
  CheckCircle2,
  KeyRound,
  X,
  RefreshCw,
} from 'lucide-react';
import {
  verifyAdminCredentials,
  sendRecoveryCode,
  resetAdminPassword,
  getAdminEmail,
  isMustChangePassword,
  setMustChangePassword,
} from '../utils/auth';
import {
  loginAdminApi,
  setAdminToken,
  touchAdminActivity,
  changeAdminPasswordApi,
} from '../utils/api';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryReason = new URLSearchParams(location.search).get('reason');
  const isSessionExpired = queryReason === 'inactivity' || queryReason === 'expired';

  const [email, setEmail] = useState(() => localStorage.getItem('bo_remembered_email') || 'admin@amoura.app');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => Boolean(localStorage.getItem('bo_remembered_email')));
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── First-Time Login Password Change State ──
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [firstLoginEmail, setFirstLoginEmail] = useState('');
  const [firstLoginCurrentPass, setFirstLoginCurrentPass] = useState('');
  const [firstLoginNewPass, setFirstLoginNewPass] = useState('');
  const [firstLoginConfirmPass, setFirstLoginConfirmPass] = useState('');
  const [showFirstLoginNewPass, setShowFirstLoginNewPass] = useState(false);
  const [firstLoginLoading, setFirstLoginLoading] = useState(false);
  const [firstLoginError, setFirstLoginError] = useState('');
  const [firstLoginSuccess, setFirstLoginSuccess] = useState('');

  // ── Forgot Password Modal State ──
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotStep, setForgotStep] = useState<1 | 2>(1);
  const [simulatedCode, setSimulatedCode] = useState<string | null>(null);
  const [enteredCode, setEnteredCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter both your email address and password.');
      return;
    }

    setLoading(true);

    try {
      // 1. Attempt API server authentication (issues JWT & 5-minute KV session)
      const apiRes = await loginAdminApi(email, password);
      if (apiRes.success && apiRes.token) {
        sessionStorage.setItem('bo_authed', '1');
        touchAdminActivity();
        if (rememberMe) {
          localStorage.setItem('bo_remembered_email', email.trim());
        } else {
          localStorage.removeItem('bo_remembered_email');
        }

        // Check if first-time password change is required
        if (apiRes.mustChangePassword) {
          setFirstLoginEmail(email.trim());
          setFirstLoginCurrentPass(password);
          setShowFirstLoginModal(true);
          return;
        }

        navigate('/dashboard');
        return;
      }

      // If the API explicitly returned invalid credentials error
      if (apiRes.code === 'INVALID_CREDENTIALS') {
        setError(apiRes.error || 'Invalid email or password. Please verify your credentials and try again.');
        setLoading(false);
        return;
      }

      // 2. Fallback to local credential verification if API server is offline
      const isValid = verifyAdminCredentials(email, password);
      if (isValid) {
        // Generate fallback token with 5-minute session
        const fallbackToken = `mock-admin-token-${Date.now()}`;
        setAdminToken(fallbackToken);
        sessionStorage.setItem('bo_authed', '1');
        touchAdminActivity();
        if (rememberMe) {
          localStorage.setItem('bo_remembered_email', email.trim());
        } else {
          localStorage.removeItem('bo_remembered_email');
        }

        if (password === 'admin123' || isMustChangePassword()) {
          setFirstLoginEmail(email.trim());
          setFirstLoginCurrentPass(password);
          setShowFirstLoginModal(true);
          return;
        }

        navigate('/dashboard');
        return;
      }

      setError(apiRes.error || 'Invalid email or password. Please verify your credentials and try again.');
    } catch {
      const isValid = verifyAdminCredentials(email, password);
      if (isValid) {
        const fallbackToken = `mock-admin-token-${Date.now()}`;
        setAdminToken(fallbackToken);
        sessionStorage.setItem('bo_authed', '1');
        touchAdminActivity();
        if (rememberMe) {
          localStorage.setItem('bo_remembered_email', email.trim());
        } else {
          localStorage.removeItem('bo_remembered_email');
        }

        if (password === 'admin123' || isMustChangePassword()) {
          setFirstLoginEmail(email.trim());
          setFirstLoginCurrentPass(password);
          setShowFirstLoginModal(true);
          return;
        }

        navigate('/dashboard');
        return;
      }
      setError('Invalid email or password. Please verify your credentials and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFirstTimeChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setFirstLoginError('');
    setFirstLoginSuccess('');

    if (!firstLoginNewPass || !firstLoginConfirmPass) {
      setFirstLoginError('Please enter and confirm your new password.');
      return;
    }
    if (firstLoginNewPass.length < 6) {
      setFirstLoginError('New password must be at least 6 characters long.');
      return;
    }
    if (firstLoginNewPass === 'admin123') {
      setFirstLoginError('Please choose a new, secure password different from "admin123".');
      return;
    }
    if (firstLoginNewPass !== firstLoginConfirmPass) {
      setFirstLoginError('New passwords do not match. Please verify.');
      return;
    }

    setFirstLoginLoading(true);
    const res = await changeAdminPasswordApi(firstLoginCurrentPass, firstLoginNewPass, firstLoginEmail);
    setFirstLoginLoading(false);

    if (res.success) {
      setFirstLoginSuccess('Password updated successfully! Welcome to the Admin Dashboard.');
      setMustChangePassword(false);
      setTimeout(() => {
        setShowFirstLoginModal(false);
        navigate('/dashboard');
      }, 1000);
    } else {
      setFirstLoginError(res.message || res.error || 'Failed to update password. Please try again.');
    }
  };

  const openForgotModal = () => {
    setForgotEmail(email || getAdminEmail());
    setForgotStep(1);
    setSimulatedCode(null);
    setEnteredCode('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotError('');
    setForgotSuccess('');
    setShowForgotModal(true);
  };

  const handleSendRecoveryCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    if (!forgotEmail.trim()) {
      setForgotError('Please enter your administrator email address.');
      return;
    }

    setForgotLoading(true);
    await new Promise((r) => setTimeout(r, 700));

    const res = sendRecoveryCode(forgotEmail);
    setForgotLoading(false);

    if (res.success && res.code) {
      setSimulatedCode(res.code);
      setForgotStep(2);
    } else {
      setForgotError(res.message);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');

    if (!enteredCode.trim()) {
      setForgotError('Please enter the 6-digit recovery code.');
      return;
    }
    if (newPassword.length < 6) {
      setForgotError('New password must be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setForgotError('New passwords do not match. Please re-enter.');
      return;
    }

    setForgotLoading(true);
    await new Promise((r) => setTimeout(r, 800));

    const res = resetAdminPassword(enteredCode, newPassword);
    setForgotLoading(false);

    if (res.success) {
      setForgotSuccess(res.message);
      setEmail(forgotEmail);
      setPassword(newPassword);
      setTimeout(() => {
        setShowForgotModal(false);
      }, 1400);
    } else {
      setForgotError(res.message);
    }
  };

  return (
    <div
      className="min-h-screen flex"
      style={{ background: 'var(--bg-base)' }}
    >
      {/* ── Left Panel ── */}
      <div
        className="hidden lg:flex flex-col justify-between w-[45%] p-12 relative overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #0d0d20 0%, #120820 50%, #1a0a0a 100%)',
          borderRight: '1px solid var(--border)',
        }}
      >
        {/* Background glow blobs */}
        <div
          className="absolute top-[-100px] left-[-100px] w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: 'var(--red)' }}
        />
        <div
          className="absolute bottom-[-100px] right-[-80px] w-72 h-72 rounded-full opacity-15 blur-3xl pointer-events-none"
          style={{ background: 'var(--purple)' }}
        />

        {/* Logo */}
        <div className="flex items-center gap-3 relative">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{ background: 'var(--red-subtle)', border: '1px solid rgba(229,57,53,0.4)' }}
          >
            <Heart size={22} className="text-red-400" fill="currentColor" />
          </div>
          <div>
            <p className="font-display font-bold text-xl" style={{ color: 'var(--text-primary)' }}>DatingApp</p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Administration Portal</p>
          </div>
        </div>

        {/* Hero text */}
        <div className="relative">
          <h1 className="font-display font-extrabold text-5xl leading-tight mb-6" style={{ color: 'var(--text-primary)' }}>
            Manage with<br />
            <span className="gradient-text">confidence.</span>
          </h1>
          <p className="text-base leading-relaxed mb-10" style={{ color: 'var(--text-secondary)', maxWidth: '360px' }}>
            The all-in-one command centre for users, subscriptions, payments, and platform safety.
          </p>

          {/* Feature list */}
          {[
            { icon: '👥', text: 'Full user lifecycle management' },
            { icon: '💳', text: 'Real-time payment & dispute resolution' },
            { icon: '⭐', text: 'Subscription tier monitoring' },
            { icon: '🛡️', text: 'Protected by Cloudflare Zero Trust' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 mb-4">
              <span className="text-xl">{item.icon}</span>
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.text}</span>
            </div>
          ))}
        </div>

        {/* Bottom tagline */}
        <div className="flex items-center gap-2 relative">
          <ShieldCheck size={15} style={{ color: 'var(--green)' }} />
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Secured via Cloudflare Access · All data encrypted at rest & in transit
          </p>
        </div>
      </div>

      {/* ── Right Panel (Form) ── */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md animate-fade-up">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-10 lg:hidden">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--red-subtle)', border: '1px solid rgba(229,57,53,0.4)' }}
            >
              <Heart size={20} className="text-red-400" fill="currentColor" />
            </div>
            <p className="font-display font-bold text-xl" style={{ color: 'var(--text-primary)' }}>DatingApp Admin</p>
          </div>

          <h2 className="font-display font-bold text-3xl mb-2" style={{ color: 'var(--text-primary)' }}>
            Welcome back
          </h2>
          <p className="text-sm mb-8" style={{ color: 'var(--text-secondary)' }}>
            Sign in to access the administration portal.
          </p>

          <form onSubmit={handleLogin} className="space-y-5">
            {/* Session Expired / Inactivity Banner */}
            {isSessionExpired && !error && (
              <div
                className="flex items-start gap-3 p-4 rounded-xl text-sm"
                style={{
                  background: 'rgba(245, 158, 11, 0.12)',
                  border: '1px solid rgba(245, 158, 11, 0.35)',
                  color: '#fbbf24',
                }}
              >
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-amber-400" />
                <div className="leading-snug">
                  <p className="font-bold text-amber-300">Session Expired (5-Min Inactivity)</p>
                  <p className="text-xs text-amber-200/90 mt-0.5">
                    Your administration session was terminated due to 5 minutes of inactivity for edge security. Please sign in again.
                  </p>
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div
                className="flex items-start gap-3 p-4 rounded-xl text-sm"
                style={{ background: 'rgba(229,57,53,0.08)', border: '1px solid rgba(229,57,53,0.2)', color: '#ef5350' }}
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="admin@amoura.app"
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-10 pr-11"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Remember / Forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded"
                  style={{ accentColor: 'var(--red)' }}
                />
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Remember me</span>
              </label>
              <button
                type="button"
                onClick={openForgotModal}
                className="text-sm font-medium hover:underline cursor-pointer"
                style={{ color: 'var(--red)', background: 'none', border: 'none', padding: 0 }}
              >
                Forgot password?
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2 h-11"
              style={{ fontSize: '15px' }}
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="animate-spin text-white" />
                  Authenticating...
                </>
              ) : (
                <>Sign in <ArrowRight size={16} /></>
              )}
            </button>
          </form>
        </div>
      </div>

      {/* ── Forgot Password Modal ── */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
          <div
            className="w-full max-w-md rounded-2xl p-6 sm:p-7 relative border shadow-2xl"
            style={{
              background: '#0d0d1a',
              borderColor: 'var(--border)',
            }}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setShowForgotModal(false)}
              className="absolute top-5 right-5 p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/60 transition-colors cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'var(--red-subtle)', border: '1px solid rgba(229,57,53,0.3)' }}
              >
                <KeyRound size={20} className="text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white font-['Outfit']">Password Recovery</h3>
                <p className="text-xs text-zinc-400">
                  {forgotStep === 1 ? 'Verify administrator identity' : 'Enter verification code & new password'}
                </p>
              </div>
            </div>

            {/* Feedback Banners */}
            {forgotError && (
              <div
                className="flex items-start gap-2.5 p-3.5 mb-4 rounded-xl text-xs"
                style={{ background: 'rgba(229,57,53,0.1)', border: '1px solid rgba(229,57,53,0.25)', color: '#ef5350' }}
              >
                <AlertCircle size={15} className="mt-0.5 shrink-0" />
                <span>{forgotError}</span>
              </div>
            )}

            {forgotSuccess && (
              <div
                className="flex items-start gap-2.5 p-3.5 mb-4 rounded-xl text-xs"
                style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#34d399' }}
              >
                <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
                <span>{forgotSuccess}</span>
              </div>
            )}

            {/* Step 1: Request Code */}
            {forgotStep === 1 && (
              <form onSubmit={handleSendRecoveryCode} className="space-y-4">
                <p className="text-xs text-zinc-300 leading-relaxed">
                  Enter your registered admin email address. A one-time verification code will be dispatched to securely reset your credentials.
                </p>

                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-1.5">Admin Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="admin@amoura.app"
                      className="w-full pl-10 pr-4 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-red-500 transition-colors"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2.5 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowForgotModal(false)}
                    className="px-4 py-2.5 text-xs font-semibold text-zinc-400 hover:text-white transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="px-5 py-2.5 text-xs font-bold rounded-xl bg-gradient-to-r from-red-600 to-pink-600 text-white hover:opacity-90 transition-opacity flex items-center gap-2 cursor-pointer"
                  >
                    {forgotLoading ? (
                      <>
                        <RefreshCw size={14} className="animate-spin text-white" />
                        Sending Code...
                      </>
                    ) : (
                      <>
                        Send Recovery Code <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: Code Verification & New Password */}
            {forgotStep === 2 && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                {simulatedCode && (
                  <div className="p-3.5 rounded-xl bg-purple-950/40 border border-purple-500/30 text-xs text-purple-200">
                    <p className="font-semibold text-purple-300">Dispatch Notification:</p>
                    <p className="mt-1">
                      A recovery code has been generated. Use code:{' '}
                      <span className="font-mono font-bold text-white bg-purple-900/60 px-2 py-0.5 rounded tracking-widest text-sm">
                        {simulatedCode}
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={() => setEnteredCode(simulatedCode)}
                      className="mt-2 text-[11px] font-bold text-pink-400 hover:underline cursor-pointer"
                    >
                      Click to auto-fill code
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-1.5">6-Digit Verification Code</label>
                  <input
                    type="text"
                    maxLength={6}
                    value={enteredCode}
                    onChange={(e) => setEnteredCode(e.target.value)}
                    placeholder="Enter 6-digit code"
                    className="w-full px-3.5 py-2.5 text-xs font-mono tracking-widest rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-red-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-1.5">New Password</label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Minimum 6 characters"
                      className="w-full pl-10 pr-10 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-red-500"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 cursor-pointer"
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-300 mb-1.5">Confirm New Password</label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      className="w-full pl-10 pr-4 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-red-500"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setForgotStep(1);
                      setForgotError('');
                    }}
                    className="text-xs text-zinc-400 hover:text-white transition-colors cursor-pointer"
                  >
                    ← Back
                  </button>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="px-5 py-2.5 text-xs font-bold rounded-xl bg-gradient-to-r from-red-600 to-pink-600 text-white hover:opacity-90 transition-opacity flex items-center gap-2 cursor-pointer"
                  >
                    {forgotLoading ? (
                      <>
                        <RefreshCw size={14} className="animate-spin text-white" />
                        Updating...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ── First-Time Login Password Change Modal ── */}
      {showFirstLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md animate-fade-in">
          <div
            className="w-full max-w-lg rounded-2xl p-6 sm:p-8 relative border shadow-2xl"
            style={{
              background: '#0d0d1a',
              borderColor: 'rgba(236, 72, 153, 0.35)',
              boxShadow: '0 25px 50px -12px rgba(236, 72, 153, 0.2)',
            }}
          >
            {/* Header Badge */}
            <div className="flex items-center gap-3.5 mb-5">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/25 shrink-0"
                style={{ background: 'linear-gradient(135deg, #e11d48, #db2777)', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <KeyRound size={22} className="text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-extrabold text-white font-['Outfit']">First-Time Login Security Setup</h3>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    Security Required
                  </span>
                </div>
                <p className="text-xs text-zinc-400 mt-0.5">
                  Administrator: <span className="text-pink-300 font-semibold">{firstLoginEmail || 'admin@amoura.app'}</span>
                </p>
              </div>
            </div>

            <div className="p-3.5 mb-5 rounded-xl text-xs bg-zinc-900/90 border border-zinc-800 text-zinc-300 leading-relaxed">
              You are signing in with the default initial credentials (<code className="px-1.5 py-0.5 rounded bg-zinc-800 text-amber-300 font-mono text-[11px]">admin123</code>). For platform security and to protect administrative audit integrity, please update your password now.
            </div>

            {/* Feedback Banners */}
            {firstLoginError && (
              <div
                className="flex items-start gap-2.5 p-3.5 mb-4 rounded-xl text-xs"
                style={{ background: 'rgba(229,57,53,0.1)', border: '1px solid rgba(229,57,53,0.25)', color: '#ef5350' }}
              >
                <AlertCircle size={15} className="mt-0.5 shrink-0" />
                <span>{firstLoginError}</span>
              </div>
            )}

            {firstLoginSuccess && (
              <div
                className="flex items-start gap-2.5 p-3.5 mb-4 rounded-xl text-xs"
                style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#34d399' }}
              >
                <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
                <span>{firstLoginSuccess}</span>
              </div>
            )}

            <form onSubmit={handleFirstTimeChangePassword} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">New Administrator Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    type={showFirstLoginNewPass ? 'text' : 'password'}
                    value={firstLoginNewPass}
                    onChange={(e) => setFirstLoginNewPass(e.target.value)}
                    placeholder="Enter new password (min. 6 characters)"
                    className="w-full pl-10 pr-10 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500 transition-colors"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowFirstLoginNewPass(!showFirstLoginNewPass)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 cursor-pointer"
                    tabIndex={-1}
                  >
                    {showFirstLoginNewPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-zinc-300 mb-1.5">Confirm New Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    type={showFirstLoginNewPass ? 'text' : 'password'}
                    value={firstLoginConfirmPass}
                    onChange={(e) => setFirstLoginConfirmPass(e.target.value)}
                    placeholder="Re-enter new password to confirm"
                    className="w-full pl-10 pr-4 py-2.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none focus:border-pink-500 transition-colors"
                    required
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowFirstLoginModal(false);
                    navigate('/dashboard');
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 text-xs font-semibold text-zinc-400 hover:text-white transition-colors cursor-pointer text-center"
                >
                  Skip for now (Remind me later)
                </button>
                <button
                  type="submit"
                  disabled={firstLoginLoading}
                  className="w-full sm:w-auto px-6 py-2.5 text-xs font-bold rounded-xl bg-gradient-to-r from-red-600 via-pink-600 to-rose-600 text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-pink-600/25"
                >
                  {firstLoginLoading ? (
                    <>
                      <RefreshCw size={14} className="animate-spin text-white" />
                      Updating Password...
                    </>
                  ) : (
                    'Save New Password & Enter'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
